// Choose something already uploaded instead of uploading it again.
//
// Every fact publishing needs — type, size, dimensions, duration — is already on
// the `media_assets` row (ADR-0005), so a library asset can be validated against
// every network's rules without the browser touching the bytes at all.

import { useEffect, useState } from "react";
import {
  listMedia, listMediaFolders, signMedia, thumbPath, thumbTargets, type MediaScope,
} from "../lib/api";
import { formatBytes } from "../lib/media";
import type { MediaAsset, MediaFolder } from "../lib/types";
import type { Brand } from "../lib/brands";
import { Thumb } from "./Thumb";

export function MediaPicker({
  workspaceId, kind, max = 1, brands = [], initialBrandId = null, onPick, onClose,
}: {
  workspaceId: string;
  /** Restricts the list when the post already has a kind to match. */
  kind?: "photo" | "video" | null;
  /**
   * How many more files the caller can take. One keeps the original behaviour —
   * a tile is a button and clicking it closes the picker. More than one turns
   * the tiles into checkboxes, because picking six photos for a carousel by
   * reopening this dialog six times is not a feature, it is a punishment.
   */
  max?: number;
  /** Brand filtering is local to this dialog. Clearing it must not change the
   *  workspace-wide brand selected by the composer/header. */
  brands?: Brand[];
  initialBrandId?: string | null;
  onPick: (assets: MediaAsset[], previewUrls: Map<string, string>) => void;
  onClose: () => void;
}) {
  const multiple = max > 1;
  // Insertion order is carousel order: the sequence somebody clicked in is the
  // sequence they meant, and re-sorting it into library order here would be
  // silently rewriting their post.
  const [chosen, setChosen] = useState<string[]>([]);
  const [assets, setAssets] = useState<MediaAsset[] | null>(null);
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  // A picker over an agency's whole library is a wall of other clients' photos,
  // so the folders from the library (ADR-0018) narrow it here too. One level of
  // filtering, not a second tree: this is a picker, not the library.
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [folderId, setFolderId] = useState<string>("");
  const [brandId, setBrandId] = useState<string>(initialBrandId ?? "");

  useEffect(() => {
    listMediaFolders(workspaceId)
      .then(setFolders)
      .catch(() => setFolders([]));   // A picker without folders still picks.
  }, [workspaceId]);

  useEffect(() => {
    const scope: MediaScope = folderId
      ? { kind: "folders", folderIds: [folderId] }
      : { kind: "all" };
    setAssets(null);
    (async () => {
      const rows = await listMedia(workspaceId, 60, 0, scope, brandId || null);
      setAssets(rows);
      setUrls(await signMedia(thumbTargets(rows), { surface: "picker" }));
    })().catch((e) => setError(e.message ?? String(e)));
  }, [workspaceId, folderId, brandId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const shown = (assets ?? []).filter((a) => {
    if (!kind) return true;
    return kind === "video" ? a.mime_type.startsWith("video/") : !a.mime_type.startsWith("video/");
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="storage-head">
          <h2 id="picker-title">Choose from your media</h2>
          {multiple && (
            <span className="muted">
              {chosen.length === 0
                ? `Pick up to ${max}`
                : `${chosen.length} of ${max} picked`}
            </span>
          )}
          <span className="spacer" />
          {brands.length > 0 && (
            <>
              <label className="visually-hidden" htmlFor="picker-brand">Brand</label>
              <select id="picker-brand" value={brandId}
                      aria-label="Filter media by brand"
                      onChange={(e) => setBrandId(e.target.value)}>
                <option value="">All brands</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </>
          )}
          {folders.length > 0 && (
            <>
              <label className="visually-hidden" htmlFor="picker-folder">Folder</label>
              <select id="picker-folder" value={folderId}
                      onChange={(e) => setFolderId(e.target.value)}>
                <option value="">All folders</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
              </select>
            </>
          )}
        </div>
        {error && <div className="err">{error}</div>}

        {!assets ? (
          <p className="muted">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="muted">
            {folderId || brandId
              ? "Nothing matches these filters yet."
              : "Nothing here yet. Upload a file above and it will appear in your media library afterwards."}
          </p>
        ) : (
          <div className="media-grid">
            {shown.map((a) => {
              const key = thumbPath(a);
              const url = key === null ? undefined : urls.get(key);
              const at = chosen.indexOf(a.id);
              // Full, and this tile is not one of the chosen: clicking it could
              // only fail, so it says so rather than doing nothing.
              const blocked = multiple && at === -1 && chosen.length >= max;

              return (
                <button
                  key={a.id}
                  className={`media-tile${at >= 0 ? " chosen" : ""}`}
                  disabled={blocked}
                  aria-pressed={multiple ? at >= 0 : undefined}
                  onClick={() => {
                    if (!multiple) return onPick([a], url ? new Map([[a.id, url]]) : new Map());
                    setChosen((c) =>
                      c.includes(a.id) ? c.filter((id) => id !== a.id)
                        : c.length >= max ? c
                        : [...c, a.id]);
                  }}
                >
                  <span className="thumb">
                    <Thumb asset={a} url={url} alt={a.original_filename ?? ""} />
                    {/* The number, not a tick: in a carousel the order is part
                        of the choice, so the badge has to say which slide this
                        will be. */}
                    {at >= 0 && <span className="pick-badge">{at + 1}</span>}
                  </span>
                  <span className="media-name">{a.original_filename ?? "Untitled"}</span>
                  <span className="muted media-meta">
                    {formatBytes(a.byte_size)}
                    {a.width ? ` · ${a.width}×${a.height}` : ""}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className={`row modal-actions${multiple ? " sticky" : ""}`}>
          <button onClick={onClose}>Cancel</button>
          {multiple && (
            <button
              className="primary"
              disabled={chosen.length === 0}
              onClick={() => {
                const byId = new Map((assets ?? []).map((a) => [a.id, a]));
                const picked = chosen.map((id) => byId.get(id)).filter((a): a is MediaAsset => !!a);
                onPick(
                  picked,
                  new Map(picked.flatMap((a) => {
                    const k = thumbPath(a);
                    const u = k === null ? undefined : urls.get(k);
                    return u ? [[a.id, u] as [string, string]] : [];
                  })),
                );
              }}
            >
              {chosen.length <= 1 ? "Use this photo" : `Use these ${chosen.length} photos`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
